import { useState } from "react";
import { toast } from "sonner";
import { CalendarCheck, MailX, Loader2, Send } from "lucide-react";
import { http, apiError, formatDateTime } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";

const first = (n) => (n || "").trim().split(/\s+/)[0] || "there";
const sign = (me) => `Kind regards,\n${me || "The Hiring Team"}\nSelby Asset Group`;
const TEMPLATES = {
  interview: (a, me) => ({
    subject: `Interview invitation: ${a.job_title} at Selby Asset Group`,
    body: `Dear ${first(a.full_name)},\n\nThank you for applying for the ${a.job_title} position at Selby Asset Group. We enjoyed reviewing your application and would like to invite you to an interview to discuss your experience and the role in more detail.\n\nThe interview details are shown below. Please reply to this email to confirm that this time works for you, or let us know if we need to find a different time.\n\nWe look forward to speaking with you.\n\n${sign(me)}`,
  }),
  not_selected: (a, me) => ({
    subject: `Your application for ${a.job_title} at Selby Asset Group`,
    body: `Dear ${first(a.full_name)},\n\nThank you for your interest in the ${a.job_title} position at Selby Asset Group and for the time you took to apply.\n\nAfter careful consideration, we have decided to move forward with other candidates whose experience more closely matches our current needs. This was not an easy decision, and we appreciate the effort you put into your application.\n\nWe encourage you to keep an eye on our Careers page and to apply for future openings that match your experience.\n\nWe wish you every success in your career.\n\n${sign(me)}`,
  }),
};
const TZ = ["Central Time (CT)", "Eastern Time (ET)", "Mountain Time (MT)", "Pacific Time (PT)", "Alaska Time (AKT)", "Hawaii Time (HT)", "UTC"];
const LOC_LABEL = { Zoom: "Meeting link", Phone: "Phone details (e.g. we will call you at …)", "In-person": "Address" };
const KIND = { interview: "Interview invitation", not_selected: "Not selected" };

const Composer = ({ a, kind, onClose, onSent }) => {
  const { user } = useAuth();
  const [t, setT] = useState(() => TEMPLATES[kind](a, user?.name));
  const [iv, setIv] = useState({ date: "", time: "", timezone: TZ[0], format: "Zoom", location: "" });
  const [busy, setBusy] = useState(false);
  const setI = (k) => (e) => setIv({ ...iv, [k]: e.target.value });
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await http.post(`/admin/careers/applications/${a.id}/email`, { kind, ...t, interview: kind === "interview" ? iv : null });
      toast.success(`Email sent to ${a.email}. Status changed to ${kind === "interview" ? "Interview" : "Rejected"}.`);
      onSent(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={send} className="space-y-4" data-testid={`composer-${kind}`}>
      <p className="text-xs text-slate-400">To: <span className="text-slate-200">{a.full_name} &lt;{a.email}&gt;</span> · Replies go to your business inbox.</p>
      <div><label className="label">Subject</label><input required className="field" value={t.subject} onChange={(e) => setT({ ...t, subject: e.target.value })} data-testid="composer-subject" /></div>
      {kind === "interview" && (
        <div className="grid sm:grid-cols-2 gap-4 border hairline p-4" data-testid="composer-interview-fields">
          <div><label className="label">Interview date *</label><input type="date" required className="field" value={iv.date} onChange={setI("date")} data-testid="composer-date" /></div>
          <div><label className="label">Time *</label><input type="time" required className="field" value={iv.time} onChange={setI("time")} data-testid="composer-time" /></div>
          <div><label className="label">Time zone *</label><select className="field" value={iv.timezone} onChange={setI("timezone")} data-testid="composer-timezone">{TZ.map((z) => <option key={z}>{z}</option>)}</select></div>
          <div><label className="label">Format *</label><select className="field" value={iv.format} onChange={setI("format")} data-testid="composer-format">{["Zoom", "Phone", "In-person"].map((f) => <option key={f}>{f}</option>)}</select></div>
          <div className="sm:col-span-2"><label className="label">{LOC_LABEL[iv.format]}</label><input className="field" placeholder={iv.format === "Zoom" ? "https://zoom.us/j/…" : ""} value={iv.location} onChange={setI("location")} data-testid="composer-location" /></div>
        </div>
      )}
      <div><label className="label">Message {kind === "interview" ? "(add a personal note anywhere)" : "(personalize if you like)"}</label><textarea required rows={12} className="field font-[inherit]" value={t.body} onChange={(e) => setT({ ...t, body: e.target.value })} data-testid="composer-body" /></div>
      <p className="text-xs text-slate-400">The email is sent in your branded layout{kind === "interview" ? " with the interview details in a table" : ""}. The applicant's status changes to <b className="text-slate-200">{kind === "interview" ? "Interview" : "Rejected"}</b> only after the email service confirms it was sent.</p>
      <div className="flex gap-3">
        <button className="btn-gold" disabled={busy} data-testid="composer-send-button">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send email</button>
        <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
      </div>
    </form>
  );
};

export const ApplicantEmails = ({ a, onChange }) => {
  const [kind, setKind] = useState(null);
  const log = [...(a.comms_log || [])].reverse();
  return (
    <section data-testid="applicant-emails">
      <p className="eyebrow mb-3">Email the applicant</p>
      <div className="flex flex-wrap gap-2">
        <button className="btn-gold !py-2 !px-4 !text-xs" onClick={() => setKind("interview")} data-testid="email-interview-button"><CalendarCheck className="w-4 h-4" /> Interview Invitation</button>
        <button className="btn-ghost !py-2 !px-4 !text-xs" onClick={() => setKind("not_selected")} data-testid="email-not-selected-button"><MailX className="w-4 h-4" /> Not Selected</button>
      </div>
      {log.length > 0 && (
        <ul className="mt-4 space-y-2" data-testid="applicant-comms-log">
          {log.map((c) => (
            <li key={c.id} className="border hairline p-3 text-xs text-slate-300" data-testid={`comms-${c.id}`}>
              <p><span className="text-slate-100">{KIND[c.kind]}</span> · <span className={c.status === "sent" ? "text-emerald-300" : "text-rose-300"}>{c.status === "sent" ? "Sent" : "Failed"}</span> · {formatDateTime(c.at)} · by {c.by}</p>
              {c.interview && <p className="mt-1 text-slate-400">{c.interview.date} {c.interview.time} {c.interview.timezone} · {c.interview.format}</p>}
              <p className="mt-1 text-slate-400 truncate">{c.subject}</p>
            </li>
          ))}
        </ul>
      )}
      <Dialog open={!!kind} onOpenChange={(v) => !v && setKind(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[var(--ink)] border-white/10">
          <DialogHeader><DialogTitle>{KIND[kind] || ""}</DialogTitle><DialogDescription>Review and edit the email, then send it.</DialogDescription></DialogHeader>
          {kind && <Composer key={kind} a={a} kind={kind} onClose={() => setKind(null)} onSent={(doc) => { setKind(null); onChange(doc); }} />}
        </DialogContent>
      </Dialog>
    </section>
  );
};
